import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Property } from "@/services/types/property";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { config } from "@/lib/config";
import {
  Bath,
  BedDouble,
  Eye,
  Home,
  MapPin,
  Ruler,
  Star,
} from "lucide-react";

interface PropertyCardProps {
  property: Property;
  variant?: "default" | "compact";
  onPropertyClick?: (property: Property) => void;
}

const PropertyCard: React.FC<PropertyCardProps> = ({
  property,
  variant = "default",
  onPropertyClick,
}) => {
  const [imageError, setImageError] = useState(false);
  const [usingPlaceholder, setUsingPlaceholder] = useState(false);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatAddress = (address: Property["address"]) => {
    return `${address.street}, ${address.city}, ${address.state}`;
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const target = e.target as HTMLImageElement;

    // If we're not already using the placeholder and the placeholder URL exists
    if (!usingPlaceholder && config.images.placeholderUrl) {
      setUsingPlaceholder(true);
      target.src = config.images.placeholderUrl;
    } else {
      // If placeholder also fails or doesn't exist, show error state
      setImageError(true);
    }
  };

  // Compact variant styles
  const isCompact = variant === "compact";

  const handleCardClick = () => {
    if (onPropertyClick) {
      onPropertyClick(property);
    }
  };

  return (
    <Card
      className={`group overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300 bg-gradient-to-br from-white to-slate-50 ${
        isCompact ? "h-full" : ""
      } ${onPropertyClick ? "cursor-pointer" : ""}`}
      onClick={handleCardClick}
    >
      {/* Property Image */}
      <div
        className={`relative ${isCompact ? "h-32" : "h-48"} overflow-hidden`}
      >
        {property.images && property.images.length > 0 && !imageError ? (
          <img
            src={property.images[0]}
            alt={property.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={handleImageError}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center">
            <div className="text-center">
              <div className="text-4xl mb-2 text-[var(--color-primary)]">
                <Home className="inline-block w-8 h-8" />
              </div>
              <span className="text-slate-500 text-sm font-medium">
                {imageError ? "Image Failed to Load" : "No Image Available"}
              </span>
            </div>
          </div>
        )}

        {/* Overlay Badges */}
        <div
          className={`absolute ${
            isCompact ? "top-2 left-2" : "top-4 left-4"
          } flex flex-col gap-2`}
        >
          {property.isFeatured && (
            <Badge
              className={`bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-primary)] text-white border-0 shadow-lg ${
                isCompact ? "text-xs py-0.5 px-2" : ""
              }`}
            >
              <Star className="w-3.5 h-3.5 mr-1 inline" />
              Featured
            </Badge>
          )}
          <Badge
            variant="secondary"
            className={`bg-white/90 text-slate-700 border-0 shadow-md ${
              isCompact ? "text-xs py-0.5 px-2" : ""
            }`}
          >
            {property.propertyType}
          </Badge>
        </div>

        {!isCompact && (
          <div className="absolute top-4 right-4">
            <Badge
              variant="secondary"
              className="bg-black/70 text-white border-0 backdrop-blur-sm flex items-center gap-1"
            >
              <Eye className="w-3.5 h-3.5" /> {property.views} views
            </Badge>
          </div>
        )}
      </div>

      <CardContent className={isCompact ? "p-3" : "p-4"}>
        {/* Property Title and Location */}
        <div className={isCompact ? "mb-2" : "mb-3"}>
          <h3
            className={`${
              isCompact ? "text-sm" : "text-base"
            } font-bold text-slate-900 ${
              isCompact ? "mb-1" : "mb-1.5"
            } line-clamp-1 group-hover:text-[var(--color-primary)] transition-colors`}
          >
            {property.title}
          </h3>
          <div
            className={`flex items-center text-slate-600 ${
              isCompact ? "text-xs" : "text-sm"
            } ${isCompact ? "mb-1" : "mb-1.5"}`}
          >
            <MapPin className="w-3 h-3 mr-1 text-[var(--color-primary)]" />
            <span className="line-clamp-1">
              {formatAddress(property.address)}
            </span>
          </div>
        </div>

        {/* Property Description - Only show in default variant */}
        {!isCompact && (
          <p className="text-slate-600 text-xs mb-3 line-clamp-2 leading-relaxed">
            {property.description}
          </p>
        )}

        {/* Price and Property Details */}
        <div className={isCompact ? "mb-2" : "mb-3"}>
          <div
            className={`flex items-center justify-between ${
              isCompact ? "mb-1.5" : "mb-2"
            }`}
          >
            <div
              className={`${
                isCompact ? "text-base" : "text-lg"
              } font-bold text-[var(--color-primary)]`}
            >
              {formatPrice(property.price)}
            </div>
            <Badge
              variant="outline"
              className={`bg-green-50 text-green-700 border-green-200 ${
                isCompact ? "text-xs py-0.5 px-1.5" : "text-xs py-0.5 px-2"
              }`}
            >
              {property.status === "active"
                ? "Available"
                : property.status === "sold"
                ? "Sold"
                : property.status === "rented"
                ? "Rented"
                : "Pending"}
            </Badge>
          </div>

          <div
            className={`flex items-center ${
              isCompact ? "justify-start gap-2" : "justify-between"
            } ${isCompact ? "text-xs" : "text-xs"} text-slate-500`}
          >
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <BedDouble className="w-3 h-3" /> {property.bedrooms} beds
              </span>
              <span className="flex items-center gap-1">
                <Bath className="w-3 h-3" /> {property.bathrooms} baths
              </span>
            </div>
            {property.squareFeet && (
              <span className="flex items-center gap-1">
                <Ruler className="w-3 h-3" />
                {isCompact
                  ? Math.round(property.squareFeet / 100) / 10 + "k"
                  : property.squareFeet.toLocaleString()}{" "}
                sq ft
              </span>
            )}
          </div>
        </div>

        {/* Amenities - Only show in default variant or show fewer in compact */}
        {property.amenities && property.amenities.length > 0 && (
          <div className={isCompact ? "mb-2" : "mb-3"}>
            <div className="flex flex-wrap gap-1 overflow-hidden">
              {property.amenities
                .slice(0, isCompact ? 2 : 2)
                .map((amenity: string, index: number) => (
                  <Badge
                    key={index}
                    variant="outline"
                    className={`bg-[var(--color-primary)]/10 text-[var(--color-primary)] border-[var(--color-primary)]/30 flex-shrink-0 ${
                      isCompact ? "text-[10px] py-0 px-1" : "text-[10px] py-0 px-1.5"
                    }`}
                  >
                    {amenity}
                  </Badge>
                ))}
              {property.amenities.length > (isCompact ? 2 : 2) && (
                <Badge
                  variant="outline"
                  className={`bg-slate-50 text-slate-600 border-slate-200 flex-shrink-0 ${
                    isCompact ? "text-[10px] py-0 px-1" : "text-[10px] py-0 px-1.5"
                  }`}
                >
                  +{property.amenities.length - (isCompact ? 2 : 2)} more
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Agent Info and Action */}
        <div
          className={`flex items-center justify-between ${
            isCompact ? "pt-2" : "pt-3"
          } border-t border-slate-100`}
        >
          {!isCompact ? (
            <>
              <div className="flex items-center gap-2">
                <Avatar className="w-6 h-6">
                  {property.agent?.avatar ? (
                    <img
                      src={property.agent.avatar}
                      alt={`${property.agent?.firstName || "Agent"} ${
                        property.agent?.lastName || ""
                      }`}
                      className="w-6 h-6 object-cover"
                    />
                  ) : (
                    <AvatarFallback className="text-xs bg-[var(--color-primary)] text-white">
                      {property.agent?.firstName?.[0] || "A"}
                      {property.agent?.lastName?.[0] || "G"}
                    </AvatarFallback>
                  )}
                </Avatar>
                <div className="text-xs">
                  <div className="text-slate-600">Listed by</div>
                  <div className="font-medium text-slate-900">
                    {property.agent?.firstName || "Agent"}{" "}
                    {property.agent?.lastName || ""}
                  </div>
                </div>
              </div>

              <Link to={`/property/${property._id}`}>
                <Button className="bg-[var(--color-primary)] hover:bg-[var(--color-primary)]/90 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 text-xs py-1.5 px-3">
                  View Details
                </Button>
              </Link>
            </>
          ) : (
            <Link to={`/property/${property._id}`} className="w-full">
              <Button className="w-full bg-[var(--color-primary)] hover:bg-[var(--color-primary)]/90 text-white border-0 shadow-md hover:shadow-lg transition-all duration-300 text-xs py-1.5">
                View Details
              </Button>
            </Link>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PropertyCard;

